import { useCallback, useEffect, useRef, useState } from "react";

const SINGLE_TARGET_ID = "__single__";
const DEFAULT_RESET_MS = 2000;

/**
 * Copy text to the clipboard and expose a self-resetting "copied" flag.
 *
 * The reset timer is cleared before it is re-armed and on unmount, so a repeat
 * copy never leaves an orphan timer and an unmounted component never receives
 * a setState.
 *
 * @param resetMs - How long the copied flag stays true. Defaults to 2000ms.
 * @returns `copy` (writes then flags), `markCopied` (flags only, for callers
 *   that perform their own write), `copiedId` (which target is flagged, or
 *   null) and `copied` (true when any target is flagged).
 */
export function useCopyToClipboard(resetMs: number = DEFAULT_RESET_MS) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const markCopied = useCallback(
    (id: string = SINGLE_TARGET_ID) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setCopiedId(id);
      timerRef.current = setTimeout(() => setCopiedId(null), resetMs);
    },
    [resetMs],
  );

  const copy = useCallback(
    async (text: string, id: string = SINGLE_TARGET_ID): Promise<boolean> => {
      if (!text || !navigator.clipboard) return false;
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        return false;
      }
      markCopied(id);
      return true;
    },
    [markCopied],
  );

  return { copiedId, copied: copiedId !== null, copy, markCopied };
}
